import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/api";

const Card = ({ children }) => (
  <div className="max-w-md mx-auto px-5 py-16">
    <div className="bg-white rounded-2xl border border-[#EBDDD3] p-8">{children}</div>
  </div>
);

export function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Card>
        <div className="text-center" data-testid="forgot-sent">
          <MailCheck className="w-12 h-12 text-[#5C6646] mx-auto mb-4" />
          <p className="text-[#5A4B45] leading-relaxed">{t("auth.resetSent")}</p>
          <Link to="/login" className="inline-block mt-6 text-[#C65338] font-semibold" data-testid="back-to-login">{t("auth.backToLogin")}</Link>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <h1 className="font-display text-2xl font-black text-[#2D231F] mb-2">{t("auth.forgotTitle")}</h1>
      <p className="text-sm text-[#8C7A70] mb-6">{t("auth.forgotDesc")}</p>
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
        <div>
          <label className="text-sm font-semibold text-[#5A4B45]">{t("auth.email")}</label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className="mt-1.5" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 font-semibold">{t("auth.sendResetLink")}</Button>
        <p className="text-center text-sm text-[#8C7A70]"><Link to="/login" className="text-[#C65338] font-semibold">{t("auth.backToLogin")}</Link></p>
      </form>
    </Card>
  );
}

export function ResetPassword() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Card>
        <p className="text-[#5A4B45] text-center" data-testid="reset-invalid">{t("auth.resetInvalid")}</p>
        <Link to="/login" className="block text-center mt-6 text-[#C65338] font-semibold">{t("auth.backToLogin")}</Link>
      </Card>
    );
  }

  if (done) {
    return (
      <Card>
        <div className="text-center" data-testid="reset-success">
          <CheckCircle2 className="w-12 h-12 text-[#5C6646] mx-auto mb-4" />
          <p className="text-[#5A4B45] leading-relaxed mb-6">{t("auth.resetSuccess")}</p>
          <Button onClick={() => nav("/login")} data-testid="reset-goto-login" className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 px-8 font-semibold">{t("auth.backToLogin")}</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <h1 className="font-display text-2xl font-black text-[#2D231F] mb-6">{t("auth.resetTitle")}</h1>
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div>
          <label className="text-sm font-semibold text-[#5A4B45]">{t("auth.newPassword")}</label>
          <Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="mt-1.5" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 font-semibold">{t("auth.setNewPassword")}</Button>
      </form>
    </Card>
  );
}
